import { useEffect, useMemo, useState } from 'react';
import { useWSMessage } from '../context/WSMessageContext';
import useParticipantsWithHost from './useParticipantsWithHost';

// Estados possíveis do sistema de turnos
export const TURN_PHASES = {
  WaitingPartner: "WAITING_PARTNER",
  Welcome: "WELCOME",
  GroundRules: "GROUND_RULES",
  SelfIntroductions: "SELF_INTRODUCTIONS",
  SelfIntroductionsA: "SELF_INTRODUCTIONS_A",
  SelfIntroductionsAtoB: "SELF_INTRODUCTIONS_A_TO_B",
  SelfIntroductionsB: "SELF_INTRODUCTIONS_B",
  SelfIntroductionsDone: "SELF_INTRODUCTIONS_DONE",
  TodayTheme: "TODAY_THEME",
  FreeChat: "FREE_CHAT",
  ACompleting: "A_COMPLETING",
  BCompleting: "B_COMPLETING",
  AQuestion: "A_QUESTION",
  BQuestion: "B_QUESTION",
  Transitioning: "TRANSITIONING",
  Goodbye: "GOODBYE",
  Checkout: "CHECKOUT",
};

// Ações que os clientes podem enviar
export const TURN_ACTIONS = {
  ACompleted: "A_COMPLETED",
  BCompleted: "B_COMPLETED",
  AAsked: "A_ASKED",
  BAsked: "B_ASKED",
  AwkwardSilence: "AWKWARD_SILENCE",
  WelcomeOk: "WELCOME_OK",
  GoodbyeOk: "GOODBYE_OK",
};

export default function useWSTurnState(debugMode = false) {
  const { messages } = useWSMessage();
  const { whoAmI } = useParticipantsWithHost();
  const [currentPhase, setCurrentPhase] = useState(null);
  const [lastCompleter, setLastCompleter] = useState(null);
  const [autoAdvanceIn, setAutoAdvanceIn] = useState(null);
  const [autoAdvanceInTimestamp, setAutoAdvanceInTimestamp] = useState(null);
  const [slideData, setSlideData] = useState();
  const [turnElapsedSeconds, setTurnElapsedSeconds] = useState(0);
  const [turnStartTime, setTurnStartTime] = useState(null);
  const [welcomeOks, setWelcomeOks] = useState([]);
  const [goodbyeOks, setGoodbyeOks] = useState([]);

  // Filtra mensagens do tipo STATE_CHANGE e room-joined
  const stateChangeMessages = useMemo(() =>
    messages.filter(msg => msg.type === 'STATE_CHANGE'),
    [messages]
  );

  const roomJoinedMessages = useMemo(() =>
    messages.filter(msg => msg.type === 'room-joined'),
    [messages]
  );

  // Sincroniza estado inicial quando entra na sala (só se não estiver em debug)
  useEffect(() => {
    if (debugMode || roomJoinedMessages.length === 0) return;

    const latestRoomJoined = roomJoinedMessages[roomJoinedMessages.length - 1];

    if (latestRoomJoined.currentState) {
      const { phase, lastCompleter: newLastCompleter, autoAdvanceIn: newAutoAdvanceIn, slideData: newSlideData, welcomeOks: newWelcomeOks, goodbyeOks: newGoodbyeOks } = latestRoomJoined.currentState;

      if (phase) {
        setCurrentPhase(phase);
        // Reseta timer quando fase muda
        setTurnStartTime(Date.now());
        setTurnElapsedSeconds(0);
      }

      if (newLastCompleter !== undefined) {
        setLastCompleter(newLastCompleter);
      }

      if (newAutoAdvanceIn !== undefined) {
        setAutoAdvanceIn(newAutoAdvanceIn);
        // Usa timestamp da mensagem se disponível, senão usa Date.now()
        const msgTimestamp = latestRoomJoined.timestamp || Date.now();
        console.log('[useWSTurnState] latestRoomJoined.timestamp:', latestRoomJoined.timestamp);
        setAutoAdvanceInTimestamp(msgTimestamp);
      }

      if (newSlideData !== undefined) {
        setSlideData(newSlideData);
      }

      if (newWelcomeOks !== undefined) {
        setWelcomeOks(newWelcomeOks);
      }

      if (newGoodbyeOks !== undefined) {
        setGoodbyeOks(newGoodbyeOks);
      }
    }
  }, [roomJoinedMessages, debugMode]);

  // Atualiza o currentPhase e lastCompleter quando recebe mudanças do servidor (só se não estiver em debug)
  useEffect(() => {
    if (debugMode || stateChangeMessages.length === 0) return;

    const latestStateChange = stateChangeMessages[stateChangeMessages.length - 1];

    // content agora é um objeto: { phase: 'A_COMPLETING', lastCompleter: 'A', autoAdvanceIn: 20, slideData: [...], welcomeOks: [...] }
    if (latestStateChange.content && typeof latestStateChange.content === 'object') {
      const { phase, lastCompleter: newLastCompleter, autoAdvanceIn: newAutoAdvanceIn, slideData: newSlideData, welcomeOks: newWelcomeOks, goodbyeOks: newGoodbyeOks } = latestStateChange.content;

      if (phase && phase !== currentPhase) {
        setCurrentPhase(phase);
        // Reseta timer quando fase muda
        setTurnStartTime(Date.now());
        setTurnElapsedSeconds(0);
        // Limpa oks quando muda de fase
        if (phase !== TURN_PHASES.Welcome) {
          setWelcomeOks([]);
        }
        if (phase !== TURN_PHASES.Goodbye) {
          setGoodbyeOks([]);
        }
      }

      if (newLastCompleter !== undefined) {
        setLastCompleter(newLastCompleter);
      }

      if (newAutoAdvanceIn !== undefined) {
        setAutoAdvanceIn(newAutoAdvanceIn);
        const msgTimestamp = latestStateChange.timestamp || Date.now();
        setAutoAdvanceInTimestamp(msgTimestamp);
      }

      if (newSlideData !== undefined) {
        setSlideData(newSlideData);
      }

      if (newWelcomeOks !== undefined && Array.isArray(newWelcomeOks)) {
        setWelcomeOks(newWelcomeOks);
      }

      if (newGoodbyeOks !== undefined && Array.isArray(newGoodbyeOks)) {
        setGoodbyeOks(newGoodbyeOks);
      }
    }
  }, [stateChangeMessages, currentPhase, debugMode]);

  // Timer que atualiza turnElapsedSeconds a cada segundo
  useEffect(() => {
    if (!turnStartTime) return;

    const interval = setInterval(() => {
      const elapsed = Math.floor((Date.now() - turnStartTime) / 1000);
      setTurnElapsedSeconds(elapsed);
    }, 1000);

    return () => clearInterval(interval);
  }, [turnStartTime]);

  // Calcula se é o turno do usuário atual
  const isMyTurn = useMemo(() => (
    (currentPhase === TURN_PHASES.ACompleting && whoAmI === 'A') ||
    (currentPhase === TURN_PHASES.BCompleting && whoAmI === 'B')
  ), [currentPhase, whoAmI]);

  return { currentPhase, lastCompleter, autoAdvanceIn, autoAdvanceInTimestamp, slideData, turnElapsedSeconds, isMyTurn, welcomeOks, goodbyeOks };
}
